import React, { useState, useEffect, useRef } from 'react';
import { AvatarSelector } from '../common/AvatarSelector';
import { PlayerAvatar } from '../common/PlayerAvatar';
import { Modal, Button, TabSwitch } from '../common';
import CharacterAssets from '@/utils/CharacterAssets';
import type { User, UpdateProfileData, UpdateProfileResult } from '@/types/auth.types';
import { 
  FaUser, 
  FaUserCircle, 
  FaLock, 
  FaSpinner,
  FaSave,
  FaBan,
  FaEnvelope,
  FaEdit,
  FaKey,
  FaCheck,
  FaExclamationTriangle
} from 'react-icons/fa';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  /** Resolves with `pendingEmail` when a new address waits for its confirmation link. */
  onUpdateProfile: (data: UpdateProfileData) => Promise<UpdateProfileResult | void>;
  isLoading?: boolean;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
  user,
  onUpdateProfile
}) => {
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    avatar: user.avatar || CharacterAssets.getDefaultCharacterId(),
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [activeTab, setActiveTab] = useState<'profile' | 'avatar' | 'password'>('profile');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  // An email change the server mailed a link for: shown until the modal closes.
  const [pendingEmail, setPendingEmail] = useState<{ to: string; from: string; alsoSaved: boolean } | null>(null);
  const pendingNoticeRef = useRef<HTMLDivElement>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    username?: string;
    email?: string;
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [fieldValidation, setFieldValidation] = useState<{
    username?: boolean;
    email?: boolean;
    currentPassword?: boolean;
    passwordMatch?: boolean;
  }>({});

  // Reset form when modal opens (not when user data changes)
  useEffect(() => {
    if (isOpen) {
      setFormData({
        username: user.username,
        email: user.email,
        avatar: user.avatar || CharacterAssets.getDefaultCharacterId(),
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError(null);
      setIsSaved(false);
      setPendingEmail(null);
      setFieldErrors({});
      setFieldValidation({});
      setActiveTab('profile');
    }
  }, [isOpen]); // Removed user dependency to prevent tab jumping on save

  // The notice replaces the save button's purpose; move focus to it so it is
  // read out rather than left on a button that just became disabled.
  useEffect(() => {
    if (pendingEmail) pendingNoticeRef.current?.focus();
  }, [pendingEmail]);

  // Validation functions
  const validateUsername = (username: string): { isValid: boolean; error?: string } => {
    if (!username.trim()) return { isValid: false, error: 'Username is required' };
    if (username.length < 3) return { isValid: false, error: 'Username must be at least 3 characters' };
    if (username.length > 50) return { isValid: false, error: 'Username must be less than 50 characters' };
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return { isValid: false, error: 'Username can only contain letters, numbers, and underscores' };
    return { isValid: true };
  };

  const validateEmail = (email: string): { isValid: boolean; error?: string } => {
    if (!email.trim()) return { isValid: false, error: 'Email is required' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return { isValid: false, error: 'Please enter a valid email address' };
    return { isValid: true };
  };

  const validateCurrentPassword = (currentPassword: string, isChangingPassword: boolean): { isValid: boolean; error?: string } => {
    if (!isChangingPassword) {
      return { isValid: true }; // Not required if not changing password
    }
    
    if (!currentPassword.trim()) {
      return { isValid: false, error: 'Current password is required when changing password' };
    }
    
    return { isValid: true };
  };

  const validatePasswords = (newPassword: string, confirmPassword: string): { isValid: boolean; errors: { newPassword?: string; confirmPassword?: string } } => {
    const errors: { newPassword?: string; confirmPassword?: string } = {};
    
    if (newPassword) {
      if (newPassword.length < 8) {
        errors.newPassword = 'Password must be at least 8 characters';
      }
    }
    
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    setIsSaved(false);

    // Real-time validation
    const newFieldErrors = { ...fieldErrors };
    const newFieldValidation = { ...fieldValidation };

    if (field === 'username') {
      const validation = validateUsername(value);
      newFieldErrors.username = validation.error;
      newFieldValidation.username = validation.isValid;
    } else if (field === 'email') {
      const validation = validateEmail(value);
      newFieldErrors.email = validation.error;
      newFieldValidation.email = validation.isValid;
    } else if (field === 'currentPassword') {
      // Check if user is changing password (either current field being updated or newPassword exists)
      const isChangingPassword = value.trim().length > 0 || formData.newPassword.length > 0;
      const validation = validateCurrentPassword(value, isChangingPassword);
      newFieldErrors.currentPassword = validation.error;
      newFieldValidation.currentPassword = validation.isValid;
    } else if (field === 'newPassword' || field === 'confirmPassword') {
      const newPassword = field === 'newPassword' ? value : formData.newPassword;
      const confirmPassword = field === 'confirmPassword' ? value : formData.confirmPassword;
      const validation = validatePasswords(newPassword, confirmPassword);
      
      if (field === 'newPassword') {
        newFieldErrors.newPassword = validation.errors.newPassword;
        
        // Re-validate current password when new password changes
        const currentPasswordValidation = validateCurrentPassword(formData.currentPassword, newPassword.length > 0);
        newFieldErrors.currentPassword = currentPasswordValidation.error;
        newFieldValidation.currentPassword = currentPasswordValidation.isValid;
      }
      if (field === 'confirmPassword') {
        newFieldErrors.confirmPassword = validation.errors.confirmPassword;
      }
      newFieldValidation.passwordMatch = !validation.errors.confirmPassword;
    }

    setFieldErrors(newFieldErrors);
    setFieldValidation(newFieldValidation);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaved(false);
    setPendingEmail(null);
    setIsSubmitting(true);

    try {
      // The address is where password resets go, so moving it takes the
      // current password, like changing the password does.
      const changesEmail = formData.email !== user.email;
      if (changesEmail && !formData.currentPassword) {
        throw new Error('Enter your current password under Security to change your email address');
      }

      // Validate password fields if changing password
      if (formData.newPassword) {
        if (!formData.currentPassword) {
          throw new Error('Current password is required when changing password');
        }
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error('New password and confirmation do not match');
        }
        if (formData.newPassword.length < 8) {
          throw new Error('New password must be at least 8 characters long');
        }
      }

      // Prepare update data
      const updateData: UpdateProfileData = {};
      const changes: string[] = [];
      
      if (formData.username !== user.username) {
        updateData.username = formData.username;
        changes.push('display name');
      }
      
      if (changesEmail) {
        updateData.email = formData.email;
        updateData.currentPassword = formData.currentPassword;
        changes.push('email address');
      }
      
      if (formData.avatar !== user.avatar) {
        updateData.avatar = formData.avatar;
        changes.push('character avatar');
      }

      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
        changes.push('password');
      }

      // Only submit if there are changes
      if (Object.keys(updateData).length > 0) {
        const result = await onUpdateProfile(updateData);
        
        // A new email is not applied yet: a link went to it, and the account
        // keeps the old address until the link is followed. Say so and stay
        // open instead of the generic success auto-close.
        if (result && result.pendingEmail) {
          const currentEmail = result.user.email;
          // Anything else in the same save (name, avatar, password) did apply.
          setPendingEmail({ to: result.pendingEmail, from: currentEmail, alsoSaved: changes.length > 1 });
          setFormData(prev => ({
            ...prev,
            email: currentEmail,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
          setFieldErrors(prev => ({ ...prev, email: undefined }));
          setFieldValidation(prev => ({ ...prev, email: undefined }));
          return;
        }

        // Show success message
        const changesText = changes.length === 1 
          ? changes[0]
          : changes.length === 2 
            ? `${changes[0]} and ${changes[1]}`
            : `${changes.slice(0, -1).join(', ')}, and ${changes[changes.length - 1]}`;
        
        // Show success state
        setIsSaved(true);
        
        // Clear password fields after successful password change
        if (formData.newPassword || formData.currentPassword) {
          setFormData(prev => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
        }
        
        // Auto-close after brief success display (800ms for better UX than 2s)
        setTimeout(() => {
          onClose();
        }, 800);
      } else {
        setError('No changes detected to save');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      setError(errorMessage);
      
      // Focus on the problematic field if it's a validation error. Without
      // case: the server says "Email already exists". The field may sit on
      // another tab than the one the user submitted from, so show its tab
      // first and focus once it has rendered.
      const lowered = errorMessage.toLowerCase();
      const field = lowered.includes('password')
        ? { id: 'currentPassword', tab: 'password' as const }
        : lowered.includes('username')
          ? { id: 'username', tab: 'profile' as const }
          : lowered.includes('email')
            ? { id: 'email', tab: 'profile' as const }
            : null;
      if (field) {
        setActiveTab(field.tab);
        setTimeout(() => document.getElementById(field.id)?.focus(), 0);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = () => {
    return (
      formData.username !== user.username ||
      formData.email !== user.email ||
      formData.avatar !== (user.avatar || CharacterAssets.getDefaultCharacterId()) ||
      formData.newPassword.length > 0
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Account Settings"
      size="3xl"
      maxHeight="95vh"
      className="bg-game-dark/95 backdrop-blur-md"
    >
      <form id="settings-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Tabs Navigation */}
        <div className="flex gap-1 bg-game-medium/30 p-1 rounded-lg">
          <TabSwitch
            isActive={activeTab === 'profile'}
            onClick={() => setActiveTab('profile')}
            disabled={isSubmitting}
            className="px-4 py-3"
          >
            <FaEdit className="text-sm" />
            <span className="hidden sm:inline">Profile</span>
          </TabSwitch>
          <TabSwitch
            isActive={activeTab === 'avatar'}
            onClick={() => setActiveTab('avatar')}
            disabled={isSubmitting}
            className="px-4 py-3"
          >
            <FaUserCircle className="text-sm" />
            <span className="hidden sm:inline">Avatar</span>
          </TabSwitch>
          <TabSwitch
            isActive={activeTab === 'password'}
            onClick={() => setActiveTab('password')}
            disabled={isSubmitting}
            className="px-4 py-3"
          >
            <FaLock className="text-sm" />
            <span className="hidden sm:inline">Security</span>
          </TabSwitch>
        </div>
        
        {/* Pending email change: the address moves only once the link is followed */}
        {pendingEmail && (
          <div
            ref={pendingNoticeRef}
            tabIndex={-1}
            role="status"
            className="bg-game-accent/10 border border-game-accent/30 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-game-golden"
          >
            <div className="flex items-start gap-3">
              <FaEnvelope className="text-game-accent flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-white">Check your inbox to finish</p>
                <p className="text-sm text-gray-200 mt-1">
                  We sent a confirmation link to <span className="font-medium text-white break-all">{pendingEmail.to}</span>.
                  Your email stays <span className="font-medium text-white break-all">{pendingEmail.from}</span> until you follow it.
                </p>
                <p className="text-xs text-gray-200 mt-1">
                  The link works for one hour.{pendingEmail.alsoSaved && ' Your other changes were saved.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="space-y-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Profile Section Header */}
                <div className="border-b border-game-medium/50 pb-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-3">
                    <FaUser className="text-game-accent" />
                    Profile Information
                  </h3>
                  <p className="text-sm text-gray-200 mt-1">Update your basic account information</p>
                </div>

                {/* Username Field */}
                <div className={`bg-game-medium/20 border rounded-lg p-5 transition-colors duration-200 ${
                  fieldErrors.username ? 'border-game-orange/50' : 
                  fieldValidation.username ? 'border-game-green/50' : 'border-game-medium/50'
                }`}>
                  <label htmlFor="username" className="block text-sm font-semibold text-white mb-3">
                    <div className="flex items-center gap-2">
                      <FaUser className="text-game-accent text-xs" />
                      Display Name
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      id="username"
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      disabled={isSubmitting}
                      required
                      minLength={3}
                      maxLength={50}
                      pattern="^[a-zA-Z0-9_]+$"
                      title="Username can only contain letters, numbers, and underscores"
                      className={`w-full px-4 py-3 pr-10 bg-game-cream border rounded-lg text-gray-900 placeholder-gray-600 focus:bg-game-cream/90 focus:border-game-golden focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        fieldErrors.username ? 'border-game-orange focus:border-game-golden' :
                        fieldValidation.username ? 'border-game-green focus:border-game-light' :
                        'border-game-medium/50 focus:border-game-accent'
                      }`}
                      placeholder="Enter your display name"
                    />
                    {/* Validation Icon */}
                    {formData.username && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {fieldErrors.username ? (
                          <FaExclamationTriangle className="text-game-orange" />
                        ) : fieldValidation.username ? (
                          <FaCheck className="text-game-green" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  
                  {/* Error Message */}
                  {fieldErrors.username && (
                    <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
                      <FaExclamationTriangle className="text-game-orange flex-shrink-0" />
                      {fieldErrors.username}
                    </div>
                  )}
                  
                  {/* Helper Text */}
                  <p className={`text-xs mt-2 transition-colors ${
                    fieldErrors.username ? 'text-game-orange' : 'text-gray-200'
                  }`}>
                    This is how other players will see you in the game
                  </p>
                </div>

                {/* Email Field */}
                <div className={`bg-game-medium/20 border rounded-lg p-5 transition-colors duration-200 ${
                  fieldErrors.email ? 'border-game-orange/50' : 
                  fieldValidation.email ? 'border-game-green/50' : 'border-game-medium/50'
                }`}>
                  <label htmlFor="email" className="block text-sm font-semibold text-white mb-3">
                    <div className="flex items-center gap-2">
                      <FaEnvelope className="text-game-accent text-xs" />
                      Email Address
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={isSubmitting}
                      required
                      className={`w-full px-4 py-3 pr-10 bg-game-cream border rounded-lg text-gray-900 placeholder-gray-600 focus:bg-game-cream/90 focus:border-game-golden focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        fieldErrors.email ? 'border-game-orange focus:border-game-golden' :
                        fieldValidation.email ? 'border-game-green focus:border-game-light' :
                        'border-game-medium/50 focus:border-game-accent'
                      }`}
                      placeholder="Enter your email address"
                    />
                    {/* Validation Icon */}
                    {formData.email && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {fieldErrors.email ? (
                          <FaExclamationTriangle className="text-game-orange" />
                        ) : fieldValidation.email ? (
                          <FaCheck className="text-game-green" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  
                  {/* Error Message */}
                  {fieldErrors.email && (
                    <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
                      <FaExclamationTriangle className="text-game-orange flex-shrink-0" />
                      {fieldErrors.email}
                    </div>
                  )}
                  
                  {/* Helper Text */}
                  <p className={`text-xs mt-2 transition-colors ${
                    fieldErrors.email ? 'text-game-orange' : 'text-gray-200'
                  }`}>
                    Used for account recovery and important notifications
                  </p>
                </div>
              </div>
            )}

            {/* Avatar Tab */}
            {activeTab === 'avatar' && (
              <div className="space-y-6">
                {/* Avatar Section Header */}
                <div className="border-b border-game-medium/50 pb-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-3">
                    <FaUserCircle className="text-game-accent" />
                    Character Selection
                  </h3>
                  <p className="text-sm text-gray-200 mt-1">Choose your character appearance in the game</p>
                </div>

                {/* Current Selection Preview */}
                <div className="bg-game-medium/20 border border-game-medium/50 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <FaUser className="text-game-accent text-xs" />
                    Current Selection
                  </h4>
                  <div className="flex items-center gap-4 p-3 bg-game-accent/10 border border-game-accent/30 rounded-lg">
                    <PlayerAvatar
                      username={user.username}
                      avatar={formData.avatar}
                      size="lg"
                      className="border-4 border-game-accent"
                    />
                    <div>
                      <p className="font-medium text-white">
                        {CharacterAssets.getCharacterInfo(formData.avatar)?.displayName || 'Unknown Character'}
                      </p>
                      <p className="text-xs text-gray-200">
                        {CharacterAssets.getCharacterInfo(formData.avatar)?.description || 'No description available'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Character Gallery */}
                <div className="bg-game-medium/20 border border-game-medium/50 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <FaUserCircle className="text-game-accent text-xs" />
                    Character Gallery
                  </h4>
                  <AvatarSelector
                    selectedAvatar={formData.avatar}
                    onAvatarSelect={(avatarId) => handleInputChange('avatar', avatarId)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <div className="space-y-6">
                {/* Security Section Header */}
                <div className="border-b border-game-medium/50 pb-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-3">
                    <FaLock className="text-game-accent" />
                    Security Settings
                  </h3>
                  <p className="text-sm text-gray-200 mt-1">Update your password and security preferences</p>
                </div>

                {/* Security Notice */}
                <div className="bg-game-accent/10 border border-game-accent/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <FaLock className="text-game-accent flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-game-light">Password Security</p>
                      <p className="text-xs text-game-teal/80 mt-1">
                        Leave password fields blank to keep your current password unchanged
                      </p>
                    </div>
                  </div>
                </div>

                {/* Current Password */}
                <div className={`bg-game-medium/20 border rounded-lg p-5 transition-colors duration-200 ${
                  fieldErrors.currentPassword ? 'border-game-orange/50' : 
                  formData.currentPassword && fieldValidation.currentPassword ? 'border-game-green/50' : 'border-game-medium/50'
                }`}>
                  <label htmlFor="currentPassword" className="block text-sm font-semibold text-white mb-3">
                    <div className="flex items-center gap-2">
                      <FaKey className="text-game-accent text-xs" />
                      Current Password
                      {formData.newPassword && (
                        <span className="text-game-orange text-xs ml-1">*</span>
                      )}
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      id="currentPassword"
                      type="password"
                      value={formData.currentPassword}
                      onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Enter your current password"
                      className={`w-full px-4 py-3 pr-10 bg-game-cream border rounded-lg text-gray-900 placeholder-gray-600 focus:bg-game-cream/90 focus:border-game-golden focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        fieldErrors.currentPassword ? 'border-game-orange focus:border-game-golden' :
                        formData.currentPassword && fieldValidation.currentPassword ? 'border-game-green focus:border-game-light' :
                        'border-game-medium/50 focus:border-game-accent'
                      }`}
                    />
                    {/* Validation Icon */}
                    {formData.currentPassword && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {fieldErrors.currentPassword ? (
                          <FaExclamationTriangle className="text-game-orange" />
                        ) : fieldValidation.currentPassword ? (
                          <FaCheck className="text-game-green" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  
                  {/* Error Message */}
                  {fieldErrors.currentPassword && (
                    <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
                      <FaExclamationTriangle className="text-game-orange flex-shrink-0" />
                      {fieldErrors.currentPassword}
                    </div>
                  )}
                  
                  {/* Helper Text */}
                  <p className={`text-xs mt-2 transition-colors ${
                    fieldErrors.currentPassword ? 'text-game-orange' : 'text-gray-200'
                  }`}>
                    {formData.newPassword 
                      ? 'Required to confirm your identity when changing password' 
                      : 'Only required when changing your password or email address'
                    }
                  </p>
                </div>

                {/* New Password */}
                <div className={`bg-game-medium/20 border rounded-lg p-5 transition-colors duration-200 ${
                  fieldErrors.newPassword ? 'border-game-orange/50' : 
                  formData.newPassword && !fieldErrors.newPassword ? 'border-game-green/50' : 'border-game-medium/50'
                }`}>
                  <label htmlFor="newPassword" className="block text-sm font-semibold text-white mb-3">
                    <div className="flex items-center gap-2">
                      <FaLock className="text-game-accent text-xs" />
                      New Password
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      type="password"
                      value={formData.newPassword}
                      onChange={(e) => handleInputChange('newPassword', e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Enter new password (leave blank to keep current)"
                      minLength={8}
                      className={`w-full px-4 py-3 pr-10 bg-game-cream border rounded-lg text-gray-900 placeholder-gray-600 focus:bg-game-cream/90 focus:border-game-golden focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        fieldErrors.newPassword ? 'border-game-orange focus:border-game-golden' :
                        formData.newPassword && !fieldErrors.newPassword ? 'border-game-green focus:border-game-light' :
                        'border-game-medium/50 focus:border-game-accent'
                      }`}
                    />
                    {/* Validation Icon */}
                    {formData.newPassword && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {fieldErrors.newPassword ? (
                          <FaExclamationTriangle className="text-game-orange" />
                        ) : (
                          <FaCheck className="text-game-green" />
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Error Message */}
                  {fieldErrors.newPassword && (
                    <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
                      <FaExclamationTriangle className="text-game-orange flex-shrink-0" />
                      {fieldErrors.newPassword}
                    </div>
                  )}
                  
                  {/* Helper Text */}
                  <p className={`text-xs mt-2 transition-colors ${
                    fieldErrors.newPassword ? 'text-game-orange' : 'text-gray-200'
                  }`}>
                    Minimum 8 characters required
                  </p>
                </div>

                {/* Confirm Password */}
                <div className={`bg-game-medium/20 border rounded-lg p-5 transition-colors duration-200 ${
                  fieldErrors.confirmPassword ? 'border-game-orange/50' : 
                  formData.confirmPassword && fieldValidation.passwordMatch ? 'border-game-green/50' : 'border-game-medium/50'
                }`}>
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-white mb-3">
                    <div className="flex items-center gap-2">
                      <FaLock className="text-game-accent text-xs" />
                      Confirm New Password
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      disabled={isSubmitting}
                      placeholder="Confirm new password"
                      minLength={8}
                      className={`w-full px-4 py-3 pr-10 bg-game-cream border rounded-lg text-gray-900 placeholder-gray-600 focus:bg-game-cream/90 focus:border-game-golden focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        fieldErrors.confirmPassword ? 'border-game-orange focus:border-game-golden' :
                        formData.confirmPassword && fieldValidation.passwordMatch ? 'border-game-green focus:border-game-light' :
                        'border-game-medium/50 focus:border-game-accent'
                      }`}
                    />
                    {/* Validation Icon */}
                    {formData.confirmPassword && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {fieldErrors.confirmPassword ? (
                          <FaExclamationTriangle className="text-game-orange" />
                        ) : fieldValidation.passwordMatch ? (
                          <FaCheck className="text-game-green" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  
                  {/* Error Message */}
                  {fieldErrors.confirmPassword && (
                    <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
                      <FaExclamationTriangle className="text-game-orange flex-shrink-0" />
                      {fieldErrors.confirmPassword}
                    </div>
                  )}
                  
                  {/* Helper Text */}
                  <p className={`text-xs mt-2 transition-colors ${
                    fieldErrors.confirmPassword ? 'text-game-orange' : 'text-gray-200'
                  }`}>
                    Must match your new password
                  </p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-game-orange/20 border border-game-orange/50 rounded-lg animate-in slide-in-from-top-2 duration-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-game-orange rounded-full flex items-center justify-center flex-shrink-0">
                    <FaExclamationTriangle className="text-white text-xs" />
                  </div>
                  <div>
                    <p className="font-semibold text-game-accent">Error</p>
                    <p className="text-sm text-white">{error}</p>
                  </div>
                </div>
              </div>
            )}
        </div>
          
        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-game-medium/30">
          <div className="text-sm text-gray-200">
            {isSubmitting ? (
              <span className="text-game-golden font-medium flex items-center gap-2">
                <FaSpinner className="animate-spin text-xs" />
                Saving your changes...
              </span>
            ) : pendingEmail && !hasChanges() ? (
              <span className="text-game-accent font-medium flex items-center gap-2">
                <FaEnvelope className="text-xs" />
                Waiting for you to confirm the new address
              </span>
            ) : isSaved ? (
              <span className="text-game-green font-medium flex items-center gap-2">
                <FaCheck className="text-xs" />
                Changes saved successfully
              </span>
            ) : hasChanges() ? (
              <span className="text-game-accent font-medium flex items-center gap-2">
                <FaEdit className="text-xs" />
                You have unsaved changes
              </span>
            ) : (
              <span>No changes to save</span>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2"
            >
              <FaBan className="text-sm" />
              {pendingEmail && !hasChanges() ? 'Close' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant={isSaved ? "secondary" : "primary"}
              disabled={isSubmitting || !hasChanges() || isSaved}
              isLoading={isSubmitting}
              className={`px-6 py-2 ${
                isSaved
                  ? 'bg-game-green hover:bg-game-green/80 text-gray-800 shadow-lg shadow-game-green/20 hover:shadow-xl'
                  : ''
              }`}
            >
              {isSaved ? (
                <>
                  <FaCheck className="text-sm" />
                  Settings Saved!
                </>
              ) : (
                <>
                  <FaSave className="text-sm" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default UserSettingsModal;